/**
 * GuardianToast — slide-in toast for proactive Omega Guardian alerts.
 *
 *  • Subscribes to /api/preload/stream (SSE) so guardian alerts surface
 *    instantly instead of waiting for the next 20-s poll.
 *  • Falls back to one-shot /api/preload poll if EventSource isn't supported.
 *  • Dedupes by alert.createdAt — same alert never shows twice in one session.
 *  • Auto-dismisses after 12 s, or on click → navigates to /mobile to inspect.
 *  • Silent on the landing page (no auth) — only mounts when user is logged in.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEEN_KEY = "zi_guardian_seen_v1";

function loadSeen() {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]")); }
  catch { return new Set(); }
}
function saveSeen(set) {
  try {
    const arr = [...set].slice(-50);  // cap so localStorage doesn't grow forever
    localStorage.setItem(SEEN_KEY, JSON.stringify(arr));
  } catch { /* quota — ignore */ }
}

export default function GuardianToast({ user }) {
  const [toast, setToast] = useState(null);    // { title, body, symbol, createdAt }
  const navigate = useNavigate();
  const seenRef = useRef(loadSeen());
  const dismissTimer = useRef(null);

  useEffect(() => {
    if (!user) return undefined;
    let es = null, alive = true, fallbackIv = null;

    const handleAlert = (alert) => {
      if (!alert || !alert.createdAt) return;
      if (seenRef.current.has(alert.createdAt)) return;
      seenRef.current.add(alert.createdAt);
      saveSeen(seenRef.current);
      setToast({
        title: alert.title || "Omega Guardian",
        body: alert.body || "",
        symbol: alert.details?.symbol,
        createdAt: alert.createdAt,
        severity: alert.severity || "medium",
      });
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
      dismissTimer.current = setTimeout(() => alive && setToast(null), 12000);
    };

    const ingest = (payload) => {
      const alerts = payload?.guardianAlerts || [];
      // Show only the most recent unseen
      for (const a of alerts) {
        if (!seenRef.current.has(a.createdAt)) {
          handleAlert(a);
          break;
        }
      }
    };

    const startFallbackPolling = () => {
      if (fallbackIv) return;
      fallbackIv = setInterval(async () => {
        try {
          const r = await axios.get(`${API}/preload`);
          if (alive) ingest(r.data);
        } catch { /* network — ignore */ }
      }, 25000);
    };

    const connect = async () => {
      if (typeof EventSource === "undefined") {
        startFallbackPolling();
        return;
      }
      try {
        // EventSource can't set Authorization headers, so it authenticates
        // via a short-lived, single-use ticket in the URL instead of a raw
        // (7-day) JWT — a ticket that leaks into browser history or a proxy
        // log is already consumed or expired by the time it'd be useful.
        const { data } = await axios.post(`${API}/auth/sse-ticket`);
        if (!alive) return;
        const url = `${API}/preload/stream?ticket=${encodeURIComponent(data.ticket)}`;
        es = new EventSource(url, { withCredentials: true });
        es.addEventListener("guardian_alert", (e) => {
          try { ingest(JSON.parse(e.data || "{}")); } catch { /* ignore */ }
        });
        es.addEventListener("snapshot", (e) => {
          try { ingest(JSON.parse(e.data || "{}")); } catch { /* ignore */ }
        });
        es.onerror = () => {
          // SSE flaked — fall back to polling
          if (es) { es.close(); es = null; }
          startFallbackPolling();
        };
      } catch {
        // Ticket fetch or EventSource constructor failed — go straight to polling
        es = null;
        if (alive) startFallbackPolling();
      }
    };
    connect();

    return () => {
      alive = false;
      if (es) es.close();
      if (fallbackIv) clearInterval(fallbackIv);
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
    };
  }, [user]);

  if (!toast) return null;

  const sev = toast.severity || "medium";
  const sevColor = sev === "high" ? "#ef4444" : sev === "low" ? "#60a5fa" : "#f59e0b";

  return (
    <div
      data-testid="guardian-toast"
      onClick={() => { setToast(null); navigate("/mobile"); }}
      role="alert"
      style={{
        position: "fixed",
        top: 16, right: 16,
        zIndex: 9100,
        maxWidth: 360,
        background: "rgba(13,17,23,0.96)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        border: `1px solid ${sevColor}55`,
        borderLeft: `4px solid ${sevColor}`,
        borderRadius: 12,
        padding: "14px 18px",
        color: "#e6eaf2",
        boxShadow: "0 18px 42px rgba(0,0,0,0.5)",
        cursor: "pointer",
        animation: "guardianSlideIn 0.32s ease-out",
        lineHeight: 1.4,
      }}
    >
      <style>{`
        @keyframes guardianSlideIn {
          from { transform: translateX(120%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 18 }}>🛡️</span>
        <div style={{ fontWeight: 800, fontSize: 13, color: sevColor, letterSpacing: 0.3, textTransform: "uppercase" }}>
          {sev === "high" ? "Critical" : sev === "low" ? "Heads up" : "Alert"}
        </div>
      </div>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{toast.title}</div>
      <div style={{ fontSize: 12, opacity: 0.85 }}>{toast.body}</div>
      <div style={{ marginTop: 8, fontSize: 11, opacity: 0.55 }}>Tap to inspect</div>
    </div>
  );
}
